import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, Menu } from 'lucide-react';
import toast from 'react-hot-toast';

interface Site {
  _id: string;
  name: string;
  code: string;
  address: string;
  postcode: string;
  city: string;
  phone: string;
  email?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  emailConfig?: {
    host: string;
    port: number;
    secure: boolean;
    user: string;
    pass: string;
    fromName: string;
  };
  smsConfig?: {
    accountSid: string;
    authToken: string;
    phoneNumber: string;
  };
  paymentConfig?: {
    provider: string;
    stripeSecretKey: string;
    stripePublishableKey: string;
    stripeLocationId: string;
    squareAccessToken: string;
    squareLocationId: string;
    squareApplicationId: string;
    squareEnvironment: string;
    sumupApiKey: string;
    sumupMerchantCode: string;
    zettleClientId: string;
    zettleClientSecret: string;
  };
  serviceCategories?: { id: string; name: string; color: string }[];
}

interface SiteFormData {
  name: string;
  code: string;
  address: string;
  postcode: string;
  city: string;
  phone: string;
  email: string;
  isActive: boolean;
  emailConfig: {
    host: string;
    port: number;
    secure: boolean;
    user: string;
    pass: string;
    fromName: string;
  };
  smsConfig: {
    accountSid: string;
    authToken: string;
    phoneNumber: string;
  };
  paymentConfig: {
    provider: string;
    stripeSecretKey: string;
    stripePublishableKey: string;
    stripeLocationId: string;
    squareAccessToken: string;
    squareLocationId: string;
    squareApplicationId: string;
    squareEnvironment: string;
    sumupApiKey: string;
    sumupMerchantCode: string;
    zettleClientId: string;
    zettleClientSecret: string;
  };
  serviceCategories: { id: string; name: string; color: string }[];
}

const emptyForm: SiteFormData = {
  name: '',
  code: '',
  address: '',
  postcode: '',
  city: '',
  phone: '',
  email: '',
  isActive: true,
  emailConfig: {
    host: 'smtp.gmail.com',
    port: 587,
    secure: false,
    user: '',
    pass: '',
    fromName: '',
  },
  smsConfig: {
    accountSid: '',
    authToken: '',
    phoneNumber: '',
  },
  paymentConfig: {
    provider: 'none',
    stripeSecretKey: '',
    stripePublishableKey: '',
    stripeLocationId: '',
    squareAccessToken: '',
    squareLocationId: '',
    squareApplicationId: '',
    squareEnvironment: 'sandbox',
    sumupApiKey: '',
    sumupMerchantCode: '',
    zettleClientId: '',
    zettleClientSecret: '',
  },
  serviceCategories: [
    { id: 'washing', name: 'Washing', color: 'blue' },
    { id: 'dry_cleaning', name: 'Dry Cleaning', color: 'purple' },
    { id: 'ironing', name: 'Ironing', color: 'green' },
    { id: 'alterations', name: 'Alterations', color: 'orange' },
    { id: 'special_care', name: 'Special Care', color: 'pink' },
  ],
};

const SiteManagement: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [formData, setFormData] = useState<SiteFormData>(emptyForm);

  const [assignUserId, setAssignUserId] = useState('');
  const [assignSiteId, setAssignSiteId] = useState('');

  const token = useMemo(() => localStorage.getItem('token'), []);

  const resetForm = () => {
    setEditingSite(null);
    setFormData(emptyForm);
  };

  const fetchSites = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/sites', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.error || 'Failed to load sites');
        setSites([]);
        return;
      }
      setSites(Array.isArray(data) ? data : data.sites || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load sites');
      setSites([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (site: Site) => {
    setEditingSite(site);
    setFormData({
      name: site.name || '',
      code: site.code || '',
      address: site.address || '',
      postcode: site.postcode || '',
      city: site.city || '',
      phone: site.phone || '',
      email: site.email || '',
      isActive: site.isActive ?? true,
      emailConfig: {
        host: site.emailConfig?.host || 'smtp.gmail.com',
        port: site.emailConfig?.port || 587,
        secure: site.emailConfig?.secure || false,
        user: site.emailConfig?.user || '',
        pass: site.emailConfig?.pass || '',
        fromName: site.emailConfig?.fromName || '',
      },
      smsConfig: {
        accountSid: site.smsConfig?.accountSid || '',
        authToken: site.smsConfig?.authToken || '',
        phoneNumber: site.smsConfig?.phoneNumber || '',
      },
      paymentConfig: {
        provider: site.paymentConfig?.provider || 'none',
        stripeSecretKey: site.paymentConfig?.stripeSecretKey || '',
        stripePublishableKey: site.paymentConfig?.stripePublishableKey || '',
        stripeLocationId: site.paymentConfig?.stripeLocationId || '',
        squareAccessToken: site.paymentConfig?.squareAccessToken || '',
        squareLocationId: site.paymentConfig?.squareLocationId || '',
        squareApplicationId: site.paymentConfig?.squareApplicationId || '',
        squareEnvironment: site.paymentConfig?.squareEnvironment || 'sandbox',
        sumupApiKey: site.paymentConfig?.sumupApiKey || '',
        sumupMerchantCode: site.paymentConfig?.sumupMerchantCode || '',
        zettleClientId: site.paymentConfig?.zettleClientId || '',
        zettleClientSecret: site.paymentConfig?.zettleClientSecret || '',
      },
      serviceCategories: site.serviceCategories && site.serviceCategories.length > 0
        ? site.serviceCategories.map(c => ({ id: c.id, name: c.name, color: c.color }))
        : [
            { id: 'washing', name: 'Washing', color: 'blue' },
            { id: 'dry_cleaning', name: 'Dry Cleaning', color: 'purple' },
            { id: 'ironing', name: 'Ironing', color: 'green' },
            { id: 'alterations', name: 'Alterations', color: 'orange' },
            { id: 'special_care', name: 'Special Care', color: 'pink' },
          ],
    });
    setShowModal(true);
  };

  const saveSite = async () => {
    try {
      const isEdit = Boolean(editingSite?._id);
      const url = isEdit
        ? `/api/sites/${editingSite!._id}`
        : '/api/sites';

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          email: formData.email.trim() ? formData.email.trim() : undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.error || 'Failed to save site');
        return;
      }

      toast.success(isEdit ? 'Site updated' : 'Site created');
      setShowModal(false);
      resetForm();
      await fetchSites();
    } catch (e) {
      console.error(e);
      toast.error('Failed to save site');
    }
  };

  const deleteSite = async (siteId: string) => {
    const ok = window.confirm('Delete this site?');
    if (!ok) return;

    try {
      const res = await fetch(`/api/sites/${siteId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.error || 'Failed to delete site');
        return;
      }
      toast.success('Site deleted');
      await fetchSites();
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete site');
    }
  };

  const assignSiteToUser = async () => {
    const userId = assignUserId.trim();
    const siteId = assignSiteId.trim();

    if (!userId) {
      toast.error('User ID is required');
      return;
    }
    if (!siteId) {
      toast.error('Select a site');
      return;
    }

    try {
      const res = await fetch(`/api/sites/users/${userId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ siteId }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.error || 'Failed to assign site');
        return;
      }
      toast.success('Site assigned to user');
      setAssignUserId('');
    } catch (e) {
      console.error(e);
      toast.error('Failed to assign site');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="sm:flex sm:justify-between sm:items-center mb-8">
          <div className="sm:flex-auto">
            <div className="flex items-center space-x-4">
              <Link
                to="/admin"
                className="inline-flex items-center text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
              <img src="/hmpos_logo.jpeg" alt="Logo" className="h-10 w-auto rounded" />
              <div>
                <p className="mt-2 text-sm text-gray-700">Create and manage your laundrette sites</p>
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-0 sm:ml-4 sm:flex-none">
            <div className="flex space-x-4">
              <Link
                to="/admin"
                className="px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              >
                <Menu className="h-5 w-5 mr-2" />
                Admin Menu
              </Link>
              <button
                onClick={openCreate}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Site
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Sites</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sites.length === 0 ? (
                  <tr>
                    <td className="px-6 py-6 text-sm text-gray-500" colSpan={6}>
                      No sites found
                    </td>
                  </tr>
                ) : (
                  sites.map((s) => (
                    <tr key={s._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{s.name}</div>
                        <div className="text-xs text-gray-500">{s._id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.code}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {s.address}, {s.city} {s.postcode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.phone}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(s.isActive ?? true)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                            }`}
                        >
                          {(s.isActive ?? true) ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openEdit(s)}
                          className="text-primary-600 hover:text-primary-900 mr-4"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => deleteSite(s._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Assign Site to User</h2>
            <p className="mt-1 text-sm text-gray-600">Paste a user ID and select a site to set that user’s site assignment.</p>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">User ID</label>
              <input
                value={assignUserId}
                onChange={(e) => setAssignUserId(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g. 65f..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Site</label>
              <select
                value={assignSiteId}
                onChange={(e) => setAssignSiteId(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select a site</option>
                {sites.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={assignSiteToUser} className="w-full btn btn-primary">
                Assign
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[680px] max-w-[95vw] max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{editingSite ? 'Edit Site' : 'Add Site'}</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700">Name</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Code</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.code}
                  onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-700">Address</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.address}
                  onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">City</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.city}
                  onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Postcode</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.postcode}
                  onChange={(e) => setFormData((p) => ({ ...p, postcode: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Phone</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.phone}
                  onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Email (optional)</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="inline-flex items-center mt-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
                    className="rounded text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>

              {/* Email Configuration */}
              <div className="col-span-2 border-t border-gray-200 pt-4 mt-2">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Email Configuration (SMTP)</h4>
              </div>
              <div>
                <label className="block text-sm text-gray-700">SMTP Host</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.emailConfig.host}
                  onChange={(e) => setFormData((p) => ({ ...p, emailConfig: { ...p.emailConfig, host: e.target.value } }))}
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm text-gray-700">Port</label>
                  <input
                    type="number"
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                    value={formData.emailConfig.port}
                    onChange={(e) => setFormData((p) => ({ ...p, emailConfig: { ...p.emailConfig, port: parseInt(e.target.value) || 587 } }))}
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.emailConfig.secure}
                      onChange={(e) => setFormData((p) => ({ ...p, emailConfig: { ...p.emailConfig, secure: e.target.checked } }))}
                      className="rounded text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">SSL/TLS</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-700">Email Username</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.emailConfig.user}
                  onChange={(e) => setFormData((p) => ({ ...p, emailConfig: { ...p.emailConfig, user: e.target.value } }))}
                  placeholder="your-email@gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Email Password / App Password</label>
                <input
                  type="password"
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.emailConfig.pass}
                  onChange={(e) => setFormData((p) => ({ ...p, emailConfig: { ...p.emailConfig, pass: e.target.value } }))}
                  placeholder="App password"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-700">From Name (displayed in emails)</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.emailConfig.fromName}
                  onChange={(e) => setFormData((p) => ({ ...p, emailConfig: { ...p.emailConfig, fromName: e.target.value } }))}
                  placeholder="e.g. Scrub a Rub Dub"
                />
              </div>

              {/* SMS Configuration */}
              <div className="col-span-2 border-t border-gray-200 pt-4 mt-2">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">SMS Configuration (Twilio)</h4>
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-700">Twilio Account SID</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.smsConfig.accountSid}
                  onChange={(e) => setFormData((p) => ({ ...p, smsConfig: { ...p.smsConfig, accountSid: e.target.value } }))}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-700">Twilio Auth Token</label>
                <input
                  type="password"
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.smsConfig.authToken}
                  onChange={(e) => setFormData((p) => ({ ...p, smsConfig: { ...p.smsConfig, authToken: e.target.value } }))}
                  placeholder="Auth token"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-700">Twilio Phone Number</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.smsConfig.phoneNumber}
                  onChange={(e) => setFormData((p) => ({ ...p, smsConfig: { ...p.smsConfig, phoneNumber: e.target.value } }))}
                  placeholder="+441234567890"
                />
              </div>

              {/* Payment Provider Configuration */}
              <div className="col-span-2 border-t border-gray-200 pt-4 mt-2">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Payment Provider (Card Machine)</h4>
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-700">Provider</label>
                <select
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.paymentConfig.provider}
                  onChange={(e) => setFormData((p) => ({ ...p, paymentConfig: { ...p.paymentConfig, provider: e.target.value } }))}
                >
                  <option value="none">None (No card payments)</option>
                  <option value="stripe">Stripe</option>
                  <option value="square">Square</option>
                  <option value="sumup">SumUp (coming soon)</option>
                  <option value="zettle">Zettle / PayPal (coming soon)</option>
                </select>
              </div>

              {/* Stripe Settings */}
              {formData.paymentConfig.provider === 'stripe' && (
                <>
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-700">Stripe Secret Key</label>
                    <input
                      type="password"
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                      value={formData.paymentConfig.stripeSecretKey}
                      onChange={(e) => setFormData((p) => ({ ...p, paymentConfig: { ...p.paymentConfig, stripeSecretKey: e.target.value } }))}
                      placeholder="sk_live_..."
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-700">Stripe Publishable Key</label>
                    <input
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                      value={formData.paymentConfig.stripePublishableKey}
                      onChange={(e) => setFormData((p) => ({ ...p, paymentConfig: { ...p.paymentConfig, stripePublishableKey: e.target.value } }))}
                      placeholder="pk_live_..."
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-700">Stripe Terminal Location ID (optional)</label>
                    <input
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                      value={formData.paymentConfig.stripeLocationId}
                      onChange={(e) => setFormData((p) => ({ ...p, paymentConfig: { ...p.paymentConfig, stripeLocationId: e.target.value } }))}
                      placeholder="tml_..."
                    />
                    <p className="mt-1 text-xs text-gray-500">Find this in Stripe Dashboard &rarr; Terminal &rarr; Locations</p>
                  </div>
                </>
              )}

              {/* Square Settings */}
              {formData.paymentConfig.provider === 'square' && (
                <>
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-700">Environment</label>
                    <select
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                      value={formData.paymentConfig.squareEnvironment}
                      onChange={(e) => setFormData((p) => ({ ...p, paymentConfig: { ...p.paymentConfig, squareEnvironment: e.target.value } }))}
                    >
                      <option value="sandbox">Sandbox (Testing)</option>
                      <option value="production">Production (Live)</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-700">Square Access Token</label>
                    <input
                      type="password"
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                      value={formData.paymentConfig.squareAccessToken}
                      onChange={(e) => setFormData((p) => ({ ...p, paymentConfig: { ...p.paymentConfig, squareAccessToken: e.target.value } }))}
                      placeholder="EAAAl..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Square Application ID</label>
                    <input
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                      value={formData.paymentConfig.squareApplicationId}
                      onChange={(e) => setFormData((p) => ({ ...p, paymentConfig: { ...p.paymentConfig, squareApplicationId: e.target.value } }))}
                      placeholder="sq0idp-..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Square Location ID</label>
                    <input
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                      value={formData.paymentConfig.squareLocationId}
                      onChange={(e) => setFormData((p) => ({ ...p, paymentConfig: { ...p.paymentConfig, squareLocationId: e.target.value } }))}
                      placeholder="L..."
                    />
                  </div>
                </>
              )}

              {/* SumUp Settings */}
              {formData.paymentConfig.provider === 'sumup' && (
                <div className="col-span-2 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">SumUp integration is coming soon. Please select Stripe or Square for now.</p>
                </div>
              )}

              {/* Zettle Settings */}
              {formData.paymentConfig.provider === 'zettle' && (
                <div className="col-span-2 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">Zettle (PayPal) integration is coming soon. Please select Stripe or Square for now.</p>
                </div>
              )}

              {/* Service Categories */}
              <div className="col-span-2 border-t border-gray-200 pt-4 mt-2">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Service Categories</h4>
                <p className="text-xs text-gray-500 mb-3">Define categories that appear in the POS. Each category has a name, slug (ID), and colour.</p>
              </div>
              <div className="col-span-2 space-y-2">
                {formData.serviceCategories.map((cat, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                      value={cat.name}
                      placeholder="Category Name"
                      onChange={(e) => {
                        const updated = [...formData.serviceCategories];
                        updated[idx] = { ...updated[idx], name: e.target.value, id: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '') };
                        setFormData((p) => ({ ...p, serviceCategories: updated }));
                      }}
                    />
                    <select
                      className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-28"
                      value={cat.color}
                      onChange={(e) => {
                        const updated = [...formData.serviceCategories];
                        updated[idx] = { ...updated[idx], color: e.target.value };
                        setFormData((p) => ({ ...p, serviceCategories: updated }));
                      }}
                    >
                      <option value="blue">🔵 Blue</option>
                      <option value="purple">🟣 Purple</option>
                      <option value="green">🟢 Green</option>
                      <option value="orange">🟠 Orange</option>
                      <option value="pink">🩷 Pink</option>
                      <option value="red">🔴 Red</option>
                      <option value="yellow">🟡 Yellow</option>
                      <option value="teal">🩵 Teal</option>
                      <option value="indigo">🟤 Indigo</option>
                      <option value="cyan">💠 Cyan</option>
                    </select>
                    <span className="text-xs text-gray-400 w-24 truncate" title={cat.id}>{cat.id}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = formData.serviceCategories.filter((_, i) => i !== idx);
                        setFormData((p) => ({ ...p, serviceCategories: updated }));
                      }}
                      className="text-red-400 hover:text-red-600 p-1"
                      title="Remove category"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormData((p) => ({
                      ...p,
                      serviceCategories: [...p.serviceCategories, { id: '', name: '', color: 'blue' }],
                    }));
                  }}
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 font-medium mt-1"
                >
                  <Plus className="w-4 h-4" /> Add Category
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveSite}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteManagement;
